import React, { useCallback, useState } from "react";
import { View, StyleSheet, ScrollView, Alert, ActivityIndicator } from "react-native";
import { PlusCircle } from "@phosphor-icons/react/dist/ssr/PlusCircle";

import {
  useGetAnnouncementsByCourseQuery,
  useDeleteAnnouncementMutation,
  AudienceType,
} from "../../../graphql/generated/graphql";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import AnnouncementModal from "../../../components/adminDashboard/Announcements/AnnouncementModal";
import DeleteResource from "../../../components/content/DeleteResource/DeleteResource";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import AdminAnnouncementCard from "../../../components/adminDashboard/AdminAnnouncementCard/AdminAnnouncementCard";
import { useAppConfig } from "../../../AppConfigProvider";

interface AdminAnnouncementsContainerProps {
  courseId: string;
}

const AdminAnnouncementsContainer: React.FC<AdminAnnouncementsContainerProps> = ({ courseId }) => {
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const appConfig = useAppConfig();
  const schoolId = appConfig.axioSchools[0];

  const {
    data: announcementsData,
    loading,
    error,
    refetch,
  } = useGetAnnouncementsByCourseQuery({
    variables: {
      courseId,
      includeInactive: true,
    },
  });

  const [deleteAnnouncement] = useDeleteAnnouncementMutation();

  const announcements = announcementsData?.getAnnouncementsByCourse?.data || [];
  // Sort announcements: active first, then expired/inactive at the bottom
  const sortedAnnouncements = [...announcements].sort((a, b) => {
    const aIsExpired = a.expirationDate && new Date(a.expirationDate) < new Date();
    const bIsExpired = b.expirationDate && new Date(b.expirationDate) < new Date();

    // If one is expired and the other isn't, expired goes to bottom
    if (aIsExpired && !bIsExpired) return 1;
    if (!aIsExpired && bIsExpired) return -1;

    // If both have same expiration status, sort by active status (active first)
    if (a.isActive !== b.isActive) {
      return b.isActive ? 1 : -1;
    }

    // If same active status, sort by creation date (newest first)
    return new Date(b.created).getTime() - new Date(a.created).getTime();
  });

  const handleAddAnnouncement = () => {
    setEditingAnnouncement({
      courses: [{ id: courseId }],
      audienceType: AudienceType.SpecificGroups,
    });
    setShowModal(true);
  };

  const handleEditAnnouncement = (announcement) => {
    console.log("Edit announcement clicked:", announcement.id);
    setEditingAnnouncement(announcement);
    setShowModal(true);
  };

  const handleDeleteAnnouncement = (announcementId: string, title: string) => {
    console.log("Delete announcement clicked:", announcementId, title);
    setDeletingAnnouncement({ id: announcementId, title });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAnnouncement) return;

    console.log("Confirming delete for:", deletingAnnouncement.id);
    setIsDeleting(true);

    try {
      await deleteAnnouncement({ variables: { id: deletingAnnouncement.id } });
      console.log("Delete successful");
      refetch();
      setShowDeleteModal(false);
      setDeletingAnnouncement(null);
    } catch (error) {
      console.error("Delete failed:", error);
      Alert.alert("Error", "Failed to delete announcement. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    console.log("Cancel delete");
    setShowDeleteModal(false);
    setDeletingAnnouncement(null);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingAnnouncement(null);
    refetch();
  };

  const isEditDisabled = useCallback((announcement: (typeof sortedAnnouncements)[number]): boolean => {
    return !(
      announcement.degrees?.length === 0 &&
      announcement.courses.length === 1 &&
      announcement.courses[0].id === courseId
    );
  }, []);

  if (loading) {
    return (
      <View style={styles.loaderContainer}>
        <ActivityIndicator size="large" color="#0000ff" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <CustomText text="Error loading announcements" size="m" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <CustomText
          text="Course Announcements"
          size="xxl"
          weight="ultraBold"
          style={styles.title}
          useTranslationText={false}
        />
        <CustomButton
          text="New Announcement"
          styleType="primary"
          size="m"
          leftIcon={<PlusCircle size={16} color="#FFFFFF" weight="bold" />}
          onPress={handleAddAnnouncement}
          useTranslationText={false}
        />
      </View>

      <CustomSpacing type="vertical" size="xl" />

      {/* Announcements List */}
      {sortedAnnouncements.length === 0 ? (
        <View style={styles.emptyState}>
          <CustomText
            text="No announcements yet"
            size="xl"
            weight="bold"
            style={styles.emptyTitle}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="s" />
          <CustomText
            text="Create your first announcement to share important updates with your students."
            size="m"
            style={styles.emptyDescription}
            useTranslationText={false}
          />
        </View>
      ) : (
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {sortedAnnouncements.map((announcement) => (
            <View key={announcement.id} style={styles.cardContainer}>
              <AdminAnnouncementCard
                announcement={announcement}
                onEdit={handleEditAnnouncement}
                onDelete={handleDeleteAnnouncement}
                disabledEdit={isEditDisabled(announcement)}
              />
            </View>
          ))}
        </ScrollView>
      )}

      {/* Edit/Create Modal */}
      <AnnouncementModal
        visible={showModal}
        onClose={handleModalClose}
        announcement={editingAnnouncement}
        schoolId={schoolId}
        disabledAudienceUpdate={true}
        onSuccess={() => {
          handleModalClose();
        }}
      />

      {/* Delete Confirmation Modal */}
      <CustomModal
        visible={showDeleteModal}
        onRequestClose={handleCancelDelete}
        ariaLabel="Delete Announcement Confirmation"
        showCloseButton={false}
        style={{ width: "auto", maxWidth: 400, height: "auto", padding: 0 }}>
        <DeleteResource
          deleteButtonClick={handleConfirmDelete}
          backButtonClick={handleCancelDelete}
          type="announcement"
        />
      </CustomModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    backgroundColor: "#fff",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    color: "#101828",
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 80,
  },
  emptyTitle: {
    color: "#101828",
    textAlign: "center",
  },
  emptyDescription: {
    color: "#6B7280",
    textAlign: "center",
    maxWidth: 400,
  },
  scrollView: {
    flex: 1,
  },
  cardContainer: {
    marginBottom: 16,
  },
});

export default AdminAnnouncementsContainer;
